import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ScrollText } from 'lucide-react';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import { getAppHomePath } from '../constants/navigation';
import { legislationService } from '../services/api';
import { useTranslation } from 'react-i18next';

function unwrapHits(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.hits || payload?.results || payload?.items || payload?.data || [];
}

const Legislation = () => {
  const { t } = useTranslation('legislation');
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [source, setSource] = useState('corpus');

  const { data: status } = useQuery(['legislation-status'], () =>
    legislationService.status().then((r) => r.data)
  );
  const { data: official } = useQuery(['legislation-official-links'], () =>
    legislationService.officialLinks().then((r) => r.data)
  );
  const { data, isFetching, error } = useQuery(
    ['legislation-search', source, submitted],
    () => {
      const fn = source === 'lexml' ? legislationService.lexmlSearch : legislationService.search;
      return fn({ q: submitted, top_k: 12 }).then((r) => r.data);
    },
    { enabled: submitted.length >= 2 }
  );
  const hits = unwrapHits(data);

  return (
    <FormPageShell>
      <PageHeader
        title={t('hubTitle')}
        description={t('hubSubtitle')}
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: t('hubTitle') },
        ]}
      />
      <p className="mt-2 text-sm text-gray-500">
        {status?.service || 'legislation-service'} · {status?.status || '—'}
      </p>
      {official?.items?.length ? (
        <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-gray-900">Links oficiais (catálogo)</h2>
          <p className="mt-1 text-xs text-gray-500">{official.disclaimer}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {official.items
              .filter((item) => ['SEI', 'DOU', 'IBAMA'].includes(item.system))
              .map((item) => (
                <li key={item.id} className="text-sm">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary-700 hover:underline"
                  >
                    {item.system} — {item.title}
                  </a>
                  <p className="text-xs text-gray-500">{item.note}</p>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
      <form
        className="mt-6 flex flex-wrap gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
      >
        <input
          className="form-input max-w-xl"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('searchPlaceholder')}
        />
        <select className="form-select max-w-xs" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="corpus">{t('corpus')}</option>
          <option value="lexml">LexML</option>
        </select>
        <button type="submit" className="btn btn-primary">
          {t('search')}
        </button>
      </form>
      <div className="mt-6 space-y-3">
        {isFetching ? <p className="text-gray-500">Buscando...</p> : null}
        {error ? <p className="text-red-600">{t('searchError')}</p> : null}
        {!isFetching && submitted && hits.length === 0 ? (
          <p className="text-gray-500">
            <ScrollText className="mb-1 inline h-5 w-5" /> {t('empty')}
          </p>
        ) : null}
        {hits.map((hit, idx) => (
          <article key={hit.id || hit.urn || idx} className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {hit.title || hit.nome || hit.urn || hit.source || t('excerpt')}
            </h3>
            <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
              {hit.snippet || hit.excerpt || hit.text || hit.ementa || '—'}
            </p>
          </article>
        ))}
      </div>
    </FormPageShell>
  );
};

export default Legislation;
